import React, { useState, useEffect } from 'react';
import { useCategoryForm } from '../hooks/useCategoryForm';
import { useTheme } from "../contexts/ThemeContext";
import { PAYMENTS_MOD, FLOW_TYPES, THEMES, THEME_COLORS } from './Constants';


// Styles de base de OperationModal/FactureModal (pour référence)
const styles = {
    modalOverlay: {
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000,
    },
    modal: {
        background: "#d18282", padding: "20px", borderRadius: "8px", width: "500px",
    },
    form: { display: "flex", flexDirection: "column", gap: "10px" },
    input: { padding: "10px", borderRadius: "4px", border: "1px solid #ccc", },
    modalActions: { display: "flex", justifyContent: "flex-end", marginTop: "10px", gap: "10px", },
    save: { background: "#007bff", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, },
    cancel: { background: "#6c757d", color: "#fff", border: "none", padding: "10px 15px", borderRadius: 4, },
    actionSmall: {
        background: '#ff9800',
        color: '#fff',
        border: 'none',
        padding: '10px 10px',
        borderRadius: '4px',
        cursor: 'pointer',
        width: '40px',
        height: '40px',
        alignSelf: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
    },
    addSmall: {
        background: "#007bff",
        color: "#fff",
        border: "none",
        padding: "5px 10px",
        borderRadius: 4,
        cursor: "pointer",
        marginTop: 0,
    },
    cancelSmall: {
        background: '#6c757d',
        color: '#fff',
        border: 'none',
        padding: '5px 10px',
        borderRadius: 4,
        cursor: 'pointer',
        marginTop: 0,
    },
};


export default function RecurrenceModal({
    show,
    currentUser,
    categories,
    accounts, 
    persons, 
    onClose,
    onNextStep,
    refreshAllData,
    initialPersonId,
    initialAccountId,
}) {
    const [formData, setFormData] = useState({
        type: 'Facture', 
        montant: '',
        day: 1,
        categorie_id: '',
        description: '',
        paiementMode: 'Prélèvement',
        statut: 'Payé',
        person_id: '', 
        account_id: '',
        sourceAccountId: '',
        destinationAccountId: '',
        is_active: true,
        payerPersonId: '',
        beneficiaryPersonId: '',
    });

    // Récupération du thème (Hook en haut !)
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    // Filtrer les personnes gérées (is_managed = true)
    const managedPersons = persons.filter(p => p.is_managed === true || p.is_managed === 1);

    // Callback pour mettre à jour formData.categorie_id
    const onCategoryCreated = (newCategoryId) => {
        setFormData(prevData => ({ ...prevData, categorie_id: newCategoryId }));
    };

    // ✅ NOUVEAU : Appel du hook
    const {
        showCategoryForm,
        newCategoryName,
        newCategoryType,
        setNewCategoryName,
        setNewCategoryType,
        setShowCategoryForm,
        handleCreateCategory,
    } = useCategoryForm(currentUser, onCategoryCreated, refreshAllData);

    // Etat pour l'affichage conditionnel
    const [showDebtCreditFields, setShowDebtCreditFields] = useState(false);

    const visibleCategories = categories.filter(c => !c.is_hidden);

    // Etat pour gérer le mode Opération (Standard/Transfert)
    const [operationMode, setOperationMode] = useState('Standard'); 

    useEffect(() => {
        // Déterminer la Personne principale par défaut
        let defaultPersonId = String(persons[0]?.id || '');

        if (initialPersonId) {
            defaultPersonId = String(initialPersonId);
        }

        // Déterminer le Compte par défaut associé à cette Personne
        const accountForPerson = accounts.find(acc => acc.personId === Number(defaultPersonId));
        let defaultAccountId = String(initialAccountId || accountForPerson?.id || '');

        // Si des personnes existent, initialiser le formulaire
        if (persons.length > 0) {
            setFormData(prev => ({
                ...prev,
                person_id: defaultPersonId,
                account_id: defaultAccountId,
                // ✅ MISE À JOUR : Initialisation des rôles D/C à la personne principale par défaut
                payerPersonId: defaultPersonId,
                beneficiaryPersonId: defaultPersonId, 
            }));
        }
    }, [persons, accounts, initialPersonId, initialAccountId]);


    if (!show) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        const updates = { [name]: name === 'is_active' ? e.target.checked : value };

        // ✅ MISE À JOUR CRITIQUE : Si la personne principale change, les rôles D/C suivent
        if (name === 'person_id') {
            updates.payerPersonId = value;
            updates.beneficiaryPersonId = value;
            
            // Si la personne change, on tente de sélectionner son premier compte
            const accountForNewPerson = accounts.find(acc => acc.personId === Number(value));
            if (accountForNewPerson) {
                updates.account_id = String(accountForNewPerson.id);
            } else {
                updates.account_id = '';
            }
        }

        setFormData(prev => ({ ...prev, ...updates }));
    };

    const isOperation = formData.type === 'Opération';
    const isTransferMode = isOperation && (operationMode === 'Transfert');

    // Handler pour le changement de type principal (Facture/Opération)
    const handleTypeChange = (e) => {
        const newType = e.target.value;
        setFormData(prev => ({ ...prev, type: newType }));
        // Si on passe à Facture, on force le mode Standard
        if (newType === 'Facture') {
            setOperationMode('Standard');
        }
    };
    
    // Handler pour le changement de sous-type (Standard/Transfert)
    const handleOperationModeChange = (e) => {
        const newMode = e.target.value;
        setOperationMode(newMode);
        
        // Réinitialisation des champs non pertinents
        if (newMode === 'Transfert') {
             setFormData(prev => ({
                 ...prev,
                 person_id: '',
                 account_id: '',
                 paiementMode: 'Virement', 
             }));
        } else { // Standard
            setFormData(prev => ({
                 ...prev,
                 sourceAccountId: '',
                 destinationAccountId: '',
                 paiementMode: 'CB',
                 // On peut laisser person_id/account_id pour qu'ils soient réinitialisés par l'useEffect, 
                 // ou réinitialisés si nécessaire
             }));
        }
    };


    const handleNext = (e) => {
        e.preventDefault();
        
        // Validation minimale...
        if (!formData.day || !formData.montant || !formData.description || !formData.categorie_id) {
            alert("Veuillez remplir les champs obligatoires.");
            return;
        }

        // Déterminer le type FINAL pour le backend
        let finalTypeForBackend;
        if (formData.type === 'Opération') {
            // ✅ Si le Type principal est 'Opération', le backend reçoit la valeur du Sous-Type (Standard ou Transfert)
            finalTypeForBackend = operationMode; 
        } else {
            finalTypeForBackend = formData.type; // Sera 'Facture'
        }
        
        const isTransferModeNow = (finalTypeForBackend === 'Transfert');

        // Conversion des valeurs et préparation des données
        const dataToSend = {
            ...formData,
            user_id: currentUser.id,
            montant: Number(formData.montant),
            day: Number(formData.day),
            categorie_id: Number(formData.categorie_id),
            
            // ✅ ENVOI DU TYPE CORRIGÉ AU BACKEND
            type: finalTypeForBackend, // <-- C'est ce que le backend attend (Facture, Standard, Transfert)
            
            // Nettoyage des champs non pertinents avant l'envoi
            person_id: isTransferModeNow ? null : Number(formData.person_id) || null,
            account_id: isTransferModeNow ? null : Number(formData.account_id) || null,
            sourceAccountId: isTransferModeNow ? Number(formData.sourceAccountId) || null : null,
            destinationAccountId: isTransferModeNow ? Number(formData.destinationAccountId) || null : null,
            
            // NOUVEAUX CHAMPS D/C pour Facture
            payerPersonId: formData.type === 'Facture' ? Number(formData.payerPersonId) || null : null,
            beneficiaryPersonId: formData.type === 'Facture' ? Number(formData.beneficiaryPersonId) || null : null,
        };

        onNextStep(dataToSend); 
    };

    // Combinaison des comptes et des personnes pour le sélecteur
    const accountsWithPersonName = accounts.map((account) => ({
        ...account,
        personName: persons.find((p) => p.id === account.personId)?.name || "N/A",
    }));

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h3>Nouvelle récurrence (Étape 1/2)</h3>
                <form onSubmit={handleNext} style={styles.form}>
                    
                    {/* 1. Type de récurrence (Facture / Opération) */}
                    <select name="type" value={formData.type} onChange={handleTypeChange} style={styles.input}>
                        <option value="Facture">Facture</option>
                        <option value="Opération">Opération</option>
                    </select>

                    {/* 2. Sous-Type d'Opération (Standard / Transfert) - Uniquement si Opération */}
                    {isOperation && (
                        <select 
                            name="operationMode" 
                            value={operationMode} 
                            onChange={handleOperationModeChange}
                            style={styles.input}
                        >
                            <option value="Standard">Standard</option>
                            <option value="Transfert">Transfert</option>
                        </select>
                    )}

                    {/* 3. Jour de facturation, Montant et Description */}
                    <input type="number" name="day" value={formData.day} onChange={handleChange} style={styles.input} min="1" max="31" placeholder="Jour de facturation (1-31)" required />
                    <input type="number" step="0.01" name="montant" value={formData.montant} onChange={handleChange} style={styles.input} placeholder="Montant" required />
                    <input type="text" name="description" value={formData.description} onChange={handleChange} style={styles.input} placeholder="Description" required />

                    {/* 4. Catégorie */}
                    {showCategoryForm ? (
                        <div style={{...styles.form, padding: '10px', border: '1px solid #ccc', borderRadius: '4px'}}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input // INPUT NOM
                                    type="text"
                                    placeholder="Nom de la nouvelle catégorie"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)} // <-- Utilise la fonction du hook
                                    style={{...styles.input, flexGrow: 1}}
                                    required
                                />

                                <select
                                    value={newCategoryType}
                                    onChange={(e) => setNewCategoryType(e.target.value)} // <-- Utilise la fonction du hook
                                    style={{ ...styles.input, width: '120px', flexShrink: 0 }} 
                                    required
                                >
                                    <option value="">Type</option>
                                    {FLOW_TYPES.map(type => ( 
                                        <option key={type.code} value={type.code}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setShowCategoryForm(false); // <-- Utilise la fonction du hook
                                        setNewCategoryName('');
                                        setNewCategoryType('D'); 
                                    }} 
                                    style={styles.cancelSmall}
                                >
                                    Annuler
                                </button>
                                <button type="button" onClick={handleCreateCategory} style={styles.addSmall}> {/* <-- Utilise la fonction du hook */}
                                    Créer
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <select 
                                name="categorie_id" 
                                value={formData.categorie_id} 
                                onChange={handleChange} 
                                style={{...styles.input, flexGrow: 1}} 
                                required
                            >
                                <option value="">Choisir une catégorie</option>
                                {/* Utiliser les catégories filtrées */}
                                {visibleCategories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                            </select>
                            <button 
                                type="button" 
                                onClick={() => setShowCategoryForm(true)}
                                style={styles.actionSmall}
                                title="Créer une nouvelle catégorie"
                            >
                                ➕
                            </button>
                        </div>
                    )}

                    {/* 5. Mode de règlement & Statut */}
                    {isTransferMode ? (
                        <input type="text" value="Virement" style={styles.input} readOnly />
                    ) : (
                        <select name="paiementMode" value={formData.paiementMode} onChange={handleChange} style={styles.input}>
                            {PAYMENTS_MOD.map(m => (<option key={m} value={m}>{m}</option>))}
                        </select>
                    )}
                    
                    <select name="statut" value={formData.statut} onChange={handleChange} style={styles.input}>
                        <option value="Non payé">Non payé</option>
                        <option value="Payé">Payé</option>
                    </select>
                    
                    {/* 6. Champs de Compte Conditionnels (Facture, Opération Standard ou Transfert) */}
                    {formData.type === 'Facture' || (isOperation && !isTransferMode) ? (
                        <>
                            {/* Facture ou Opération Standard : Personne et Compte unique */}
                            <select name="person_id" value={formData.person_id} onChange={handleChange} style={styles.input} required>
                                <option value="">Choisir une personne</option>
                                {managedPersons.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                            </select>
                            <select name="account_id" value={formData.account_id} onChange={handleChange} style={styles.input} required>
                                <option value="">Choisir un compte</option>
                                {accounts.filter(acc => acc.personId === Number(formData.person_id)).map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>

                            {/* NOUVEAUX CHAMPS Dettes/Créances - Uniquement pour les FACTURES */}
                            {formData.type === 'Facture' && (
                                <div style={{ border: `1px solid ${themeColors.secondary}20`, borderRadius: '4px', padding: '10px', marginTop: '10px' }}>
                                    
                                    <button 
                                        type="button"
                                        onClick={() => setShowDebtCreditFields(!showDebtCreditFields)}
                                        style={{ 
                                            ...styles.secondaryButton, // Vous devrez définir ce style ou utiliser un style simple
                                            backgroundColor: '#f0f0f0',
                                            color: '#333',
                                            padding: '8px 12px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            marginBottom: '10px',
                                            width: '100%',
                                            textAlign: 'left',
                                        }}
                                    >
                                        {showDebtCreditFields ? '▼ Masquer' : '► Afficher'} Gestion Dettes/Créances
                                    </button>

                                    {showDebtCreditFields && (
                                        <div style={styles.form}>                                                                                  
                                            {/* Payeur (Créancier potentiel) */}
                                            <select 
                                                name="payerPersonId" 
                                                value={formData.payerPersonId} 
                                                onChange={handleChange} 
                                                style={styles.input} 
                                                required
                                            >
                                                <option value="">Qui paie la facture ? (Créancier)</option>
                                                {/* Toutes les personnes (gérées et non gérées) */}
                                                {persons.map((p) => (<option key={`payer-${p.id}`} value={p.id}>{p.name} (Paiement)</option>))}
                                            </select>

                                            {/* Bénéficiaire (Débiteur potentiel) */}
                                            <select 
                                                name="beneficiaryPersonId" 
                                                value={formData.beneficiaryPersonId} 
                                                onChange={handleChange} 
                                                style={styles.input} 
                                                required
                                            >
                                                <option value="">Qui est le bénéficiaire ? (Débiteur)</option>
                                                {/* Toutes les personnes (gérées et non gérées) */}
                                                {persons.map((p) => (<option key={`benef-${p.id}`} value={p.id}>{p.name} (Bénéficiaire)</option>))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        isTransferMode && (
                            <>
                                {/* Opération Transfert : Compte Source et Destination */}
                                <select name="sourceAccountId" value={formData.sourceAccountId} onChange={handleChange} style={styles.input} required>
                                    <option value="">Compte Source</option>
                                    {accountsWithPersonName.map((acc) => (<option key={acc.id} value={acc.id}>{acc.personName} - {acc.name}</option>))}
                                </select>
                                <select name="destinationAccountId" value={formData.destinationAccountId} onChange={handleChange} style={styles.input} required>
                                    <option value="">Compte Destination</option>
                                    {accountsWithPersonName.filter(acc => acc.id !== Number(formData.sourceAccountId)).map((acc) => (
                                        <option key={acc.id} value={acc.id}>{acc.personName} - {acc.name}</option>
                                    ))}
                                </select>
                            </>
                        )
                    )}
                    
                    {/* 7. Toggle Actif/Suspendu (par défaut à Actif lors de la création) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label>Récurrence Active :</label>
                        <input 
                            type="checkbox" 
                            name="is_active" 
                            checked={formData.is_active} 
                            onChange={handleChange} 
                            style={{ width: 'auto' }}
                        />
                    </div>

                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={styles.cancel}>Annuler</button>
                        <button type="submit" style={styles.save}>
                            Suivant: Choisir les mois
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}